// src/components/GuideManagementModule.js
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext'; // Importa useAuth para el usuario logueado

function GuideManagementModule() {
  const { user } = useAuth(); // Obtiene el usuario del contexto
  const [orderReference, setOrderReference] = useState(''); // Estado para el número de pedido/referencia
  const [foundOrder, setFoundOrder] = useState(null); // Estado para almacenar el pedido encontrado
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Helper para formatear la guía de Mercado Libre (MEL)
  const formatMercadoLibreGuide = (guideNumber) => {
    if (!guideNumber) return 'N/A';
    const match = guideNumber.match(/MEL(\d+)(?:LMXDF01|FMDOF01)/);
    if (match && match[1]) {
      return match[1];
    }
    return guideNumber; // Si no es formato MEL, devuelve el número original
  };

  // Función para buscar el pedido por su referencia
  const handleSearchOrder = async (e) => {
    e.preventDefault();
    if (!orderReference.trim()) {
      setMessage('Por favor, ingresa un número de pedido o referencia.');
      return;
    }

    setLoading(true);
    setMessage('');
    setFoundOrder(null); // Limpiar el pedido anterior
    try {
      // Llama al nuevo endpoint para buscar por referencia
      const response = await fetch(`/api/orders/search-by-reference?reference=${orderReference.trim()}`);
      const data = await response.json();

      if (response.ok) {
        if (data.order) {
          setFoundOrder(data.order);
          setMessage('Pedido encontrado.');
        } else {
          setMessage('Pedido no encontrado para la referencia proporcionada.');
        }
      } else {
        setMessage(`Error al buscar pedido: ${data.mensaje || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error('Error de red al buscar pedido:', err);
      setMessage('Error de conexión con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  // Función para imprimir una guía (llamando al backend) y luego marcarla como impresa en la DB
  const handlePrintAndMarkGuide = async (guideToPrintId, guideNumberToPrint) => {
    if (!foundOrder || !foundOrder.id) return;

    const performedByUserId = user?.id || null;
    const performedByUsername = user?.nombre || 'Desconocido'; 

    setMessage('Obteniendo guía y marcando como impresa...');

    try {
      let isNewGuideGenerated = false;
      let finalGuideId = guideToPrintId;
      let finalGuideNumber = guideNumberToPrint;

      // Si guideToPrintId es null, significa que se quiere generar una nueva guía
      if (!guideToPrintId) {
        const generateResponse = await fetch(`/api/orders/${foundOrder.id}/print-guide`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            performed_by_user_id: performedByUserId,
            performed_by_username: performedByUsername
          })
        });
        const generateData = await generateResponse.json();

        if (!generateResponse.ok) {
          setMessage(`Error al generar nueva guía: ${generateData.mensaje || 'Error desconocido'}`);
          return;
        }
        setMessage(generateData.mensaje);
        finalGuideId = generateData.guide_id;
        finalGuideNumber = generateData.numero_guia_generada;
        isNewGuideGenerated = true;
      }

      if (!finalGuideNumber || finalGuideNumber === 'N/A') {
        setMessage('Error: No hay un número de guía válido para imprimir.');
        return;
      }

      // Llamar al backend para obtener el PDF de la guía
      const response = await fetch(`/api/orders/${foundOrder.id}/label?folios=${foundOrder.folio_autoazur}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error al obtener el PDF de la guía: ${errorText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Abrir el PDF en una nueva ventana/pestaña para impresión
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          // printWindow.print(); // Opcional: imprimir directamente
        };
      } else {
        setMessage('El navegador bloqueó la ventana de impresión. Por favor, permite pop-ups para este sitio.');
      }

      // Marcar la guía como impresa en la base de datos (si no fue recién generada)
      if (!isNewGuideGenerated && finalGuideId) {
           const markPrintedResponse = await fetch(`/api/orders/${foundOrder.id}/print-guide`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    guide_id: finalGuideId,
                    performed_by_user_id: performedByUserId,
                    performed_by_username: performedByUsername
                })
            });
            const markPrintedData = await markPrintedResponse.json();
            if (!markPrintedResponse.ok) {
                console.error('Error al marcar guía como impresa después de imprimir:', markPrintedData.mensaje);
                setMessage(`Guía impresa, pero error al marcar como impresa en DB: ${markPrintedData.mensaje}`);
            }
      }

      // Refrescar los detalles del pedido para obtener las guías actualizadas
      const updatedOrderResponse = await fetch(`/api/orders/search-by-reference?reference=${orderReference.trim()}`);
      const updatedOrderData = await updatedOrderResponse.json();
      if (updatedOrderResponse.ok) { setFoundOrder(updatedOrderData.order); }

      setMessage('Guía obtenida e impresa. Estado actualizado.');

    } catch (err) {
      console.error('Error al imprimir guía:', err);
      setMessage(`Error al imprimir guía: ${err.message}`);
    } finally {
        setLoading(false);
    }
  };


  return (
    <>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Módulo de Gestión de Guías</h2>

      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Buscar Pedido y Gestionar Guías</h3>
        <form onSubmit={handleSearchOrder} className="flex items-center space-x-4 mb-4">
          <input
            type="text"
            className="shadow appearance-none border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:shadow-outline flex-grow"
            placeholder="Ingresa número de pedido o referencia (ej. M2500696, 2000008656073433)..."
            value={orderReference}
            onChange={(e) => setOrderReference(e.target.value)}
            disabled={loading}
          />
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg focus:outline-none focus:shadow-outline transition duration-200 whitespace-nowrap"
            disabled={loading}
          >
            {loading ? 'Buscando...' : 'Buscar Pedido'}
          </button>
        </form>

        {message && (
          <div className={`p-2 mb-4 text-center rounded-lg ${message.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
            {message}
          </div>
        )}

        {foundOrder && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg shadow-inner">
            <h4 className="text-lg font-semibold text-gray-800 mb-3">Detalles del Pedido Encontrado</h4>
            <p className="text-gray-700"><strong>Referencia Plataforma:</strong> {foundOrder.referencia_plataforma}</p>
            <p className="text-gray-700"><strong>Folio AutoAzur:</strong> {foundOrder.folio_autoazur}</p>
            <p className="text-gray-700"><strong>Cliente:</strong> {foundOrder.cliente_nombre}</p>
            <p className="text-gray-700"><strong>Estatus Interno:</strong> {foundOrder.estado_interno}</p>
            <p className="text-gray-700"><strong>Plataforma:</strong> {foundOrder.canal_origen}</p>
            <p className="text-gray-700"><strong>Total Llantas:</strong> {foundOrder.total_llantas}</p>
            <p className="text-gray-700"><strong>Descripción Llantas:</strong> {foundOrder.descripcion_llantas}</p>
            
            <div className="mt-4 border-t pt-4">
              <h5 className="font-semibold text-gray-800 mb-2">Guía(s) Asociada(s):</h5>
              {foundOrder.guides && foundOrder.guides.length > 0 ? (
                foundOrder.guides.map((guide, index) => (
                  <div key={guide.id || index} className="flex items-center justify-between text-sm text-gray-700 mb-1 p-2 border rounded-md">
                    <span>
                      Guía: {foundOrder.canal_origen === 'MERCADO LIBRE' ? formatMercadoLibreGuide(guide.guide_number) : guide.guide_number || 'N/A'}
                      {guide.print_status === 1 ? (
                        <span className="ml-2 text-green-600 font-medium">Impresa &#10003;</span>
                      ) : (
                        <span className="ml-2 text-red-600 font-medium">No Impresa &#10007;</span>
                      )}
                    </span>
                    <button
                      onClick={() => handlePrintAndMarkGuide(guide.id, guide.guide_number)}
                      className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-1 px-2 rounded-lg text-xs transition duration-200"
                      title="Imprimir esta guía y marcarla como impresa"
                    >
                      Imprimir Guía
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-600">No hay guías asociadas a este pedido.</p>
              )}
              <button
                onClick={() => handlePrintAndMarkGuide(null, `GUIA-${foundOrder.id}-${Date.now()}`)} // Genera nueva guía
                className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg text-sm transition duration-200"
              >
                Generar Nueva Guía e Imprimir
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default GuideManagementModule;
